import React, { useState, useEffect } from 'react';
import { doc, updateDoc, setDoc, getDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { motion, AnimatePresence } from 'framer-motion';
import { db, auth } from '../firebase';
import { configDoc } from '../db';
import { UserProfile, Screen } from '../App';
import { inferGender, ProfileGender } from '../utils/gender';

interface Props {
  user: UserProfile;
  navigate: (s: Screen) => void;
  onProfileUpdate: (updated: Partial<UserProfile>) => void;
}

const AVATARS = ['👩🏻', '👩🏼', '👩🏽', '👩🏾', '👩🏿', '👨🏻', '👨🏼', '👨🏽', '👨🏾', '👨🏿', '🧑🏻', '🧑🏼', '🧑🏽', '🧑🏾', '🧑🏿', '🦸‍♀️', '🦸‍♂️', '🧚‍♀️', '🧚‍♂️', '🌸'];

export default function Settings({ user, navigate, onProfileUpdate }: Props) {
  const [name, setName] = useState(user.displayName);
  const [avatar, setAvatar] = useState('');
  const [gender, setGender] = useState<ProfileGender | ''>(user.gender || inferGender(user) || '');
  const [startDate, setStartDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getDoc(doc(db, 'users', user.uid)).then(snap => {
      if (snap.exists()) {
        const data = snap.data();
        setAvatar(data.avatar || '');
        setGender(data.gender || inferGender({ displayName: data.displayName, email: data.email }) || '');
      }
    });
    getDoc(configDoc(user.coupleId)).then(snap => {
      if (snap.exists() && snap.data().startDate) setStartDate(snap.data().startDate);
    });
  }, [user.uid, user.coupleId]);

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await updateDoc(doc(db, 'users', user.uid), {
      displayName: name.trim(),
      ...(gender ? { gender } : {}),
      ...(avatar ? { avatar } : {}),
    });
    if (startDate) {
      await setDoc(configDoc(user.coupleId), { startDate }, { merge: true });
    }
    onProfileUpdate({ displayName: name.trim(), ...(gender ? { gender } : {}) });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div>
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('us')}>←</button>
        <h1 style={styles.title}>Ayarlar</h1>
      </div>

      <div style={styles.body}>
        {/* Profile */}
        <div style={styles.section}>
          <p style={styles.sectionLabel}>Profil</p>

          <div style={styles.avatarRow}>
            <div style={styles.avatarPreview}>{avatar || '🌸'}</div>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="İsmin"
              style={styles.nameInput}
              maxLength={20}
            />
          </div>

          <div style={styles.avatarGrid}>
            {AVATARS.map(em => (
              <motion.button
                key={em}
                whileTap={{ scale: 0.88 }}
                onClick={() => setAvatar(em)}
                style={{
                  ...styles.avatarBtn,
                  background: avatar === em ? 'rgba(233,30,140,0.2)' : 'rgba(255,255,255,0.06)',
                  borderColor: avatar === em ? 'rgba(233,30,140,0.6)' : 'rgba(255,255,255,0.1)',
                }}
              >
                {em}
              </motion.button>
            ))}
          </div>

          <div style={styles.genderRow}>
            <button type="button" onClick={() => setGender('female')} style={{ ...styles.genderBtn, ...(gender === 'female' ? styles.genderBtnActive : {}) }}>Kadın</button>
            <button type="button" onClick={() => setGender('male')} style={{ ...styles.genderBtn, ...(gender === 'male' ? styles.genderBtnActive : {}) }}>Erkek</button>
          </div>
        </div>

        {/* Birliktelik tarihi */}
        <div style={styles.section}>
          <p style={styles.sectionLabel}>Birliktelik Tarihi</p>
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            max={new Date().toISOString().split('T')[0]}
            style={styles.dateInput}
          />
          {startDate && (
            <p style={styles.dateHint}>
              {Math.floor((Date.now() - new Date(startDate + 'T00:00:00').getTime()) / 86400000)} gündür birliktesiniz 💕
            </p>
          )}
        </div>

        <div style={styles.trustSection}>
          <p style={styles.sectionLabel}>Gizlilik ve Güven</p>
          <div style={styles.trustList}>
            <div style={styles.trustRow}>
              <span style={styles.trustIndex}>01</span>
              <p style={styles.trustText}>Bu alan çiftinize ait koleksiyonlarda tutulur; günlük izler başka kullanıcı ekranlarında gösterilmez.</p>
            </div>
            <div style={styles.trustRow}>
              <span style={styles.trustIndex}>02</span>
              <p style={styles.trustText}>Bildirimler kısa ve mahrem kalacak şekilde yazılır; uzun cevaplar bildirim metnine taşınmaz.</p>
            </div>
            <div style={styles.trustRow}>
              <span style={styles.trustIndex}>03</span>
              <p style={styles.trustText}>Profil, tarih ve Minik karakteri burada değiştirilebilir; çıkış işlemi bu cihazdaki oturumu kapatır.</p>
            </div>
          </div>
        </div>

        {/* Kaydet */}
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={save}
          disabled={saving || !name.trim()}
          style={styles.saveBtn}
        >
          <AnimatePresence mode="wait">
            {saved ? (
              <motion.span key="saved" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
                ✅ Kaydedildi
              </motion.span>
            ) : (
              <motion.span key="save">
                {saving ? 'Kaydediliyor...' : '💾 Kaydet'}
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>

        {/* Karakter */}
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={() => navigate('character-creator')}
          style={styles.recreateBtn}
        >
          👾 Karakteri Yeniden Oluştur
        </motion.button>

        {/* Çıkış */}
        <button onClick={() => signOut(auth)} style={styles.logoutBtn}>
          Çıkış Yap
        </button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  title: { fontFamily: "'Playfair Display',serif", fontSize: 22 },
  body: { padding: '8px 16px 80px', display: 'flex', flexDirection: 'column', gap: 20 },
  section: {
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)',
    borderRadius: 20, padding: '16px 16px 20px', display: 'flex', flexDirection: 'column', gap: 12,
  },
  sectionLabel: { fontSize: 11, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' as const, letterSpacing: 1 },
  trustSection: {
    background: 'linear-gradient(145deg, rgba(103,232,249,0.11), rgba(255,255,255,0.04))',
    border: '1px solid rgba(103,232,249,0.16)',
    borderRadius: 22,
    padding: '16px 16px 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  trustList: { display: 'grid', gap: 10 },
  trustRow: { display: 'grid', gridTemplateColumns: '30px 1fr', gap: 10, alignItems: 'start' },
  trustIndex: { color: '#67E8F9', fontSize: 11, fontWeight: 900, lineHeight: 1.5 },
  trustText: { color: 'rgba(255,255,255,0.62)', fontSize: 13, lineHeight: 1.45, margin: 0 },
  avatarRow: { display: 'flex', alignItems: 'center', gap: 12 },
  avatarPreview: {
    width: 48, height: 48, borderRadius: '50%', fontSize: 26, flexShrink: 0,
    background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  nameInput: {
    flex: 1, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 12, padding: '10px 14px', color: 'white', fontSize: 15, outline: 'none',
  },
  avatarGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 7 },
  avatarBtn: {
    fontSize: 22, padding: '8px 0', borderRadius: 10, border: '1px solid',
    cursor: 'pointer', transition: 'background 0.15s',
  },
  genderRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  genderBtn: {
    minHeight: 44,
    borderRadius: 14,
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.06)',
    color: 'rgba(255,255,255,0.68)',
    fontSize: 14,
    fontWeight: 800,
    cursor: 'pointer',
  },
  genderBtnActive: {
    background: 'rgba(233,30,140,0.18)',
    border: '1px solid rgba(233,30,140,0.5)',
    color: 'white',
  },
  dateInput: {
    width: '100%', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 12, padding: '12px 14px', color: 'white', fontSize: 15, outline: 'none',
    boxSizing: 'border-box' as const,
  },
  dateHint: { fontSize: 13, color: 'rgba(255,255,255,0.45)', textAlign: 'center' as const },
  saveBtn: {
    width: '100%', padding: '15px 24px',
    background: 'linear-gradient(135deg, #E91E8C, #9C27B0)',
    border: 'none', borderRadius: 20,
    color: 'white', fontSize: 16, fontWeight: 700, cursor: 'pointer',
    boxShadow: '0 6px 24px rgba(233,30,140,0.3)',
  },
  recreateBtn: {
    width: '100%', padding: '14px 24px',
    background: 'rgba(156,39,176,0.12)', border: '1px solid rgba(156,39,176,0.35)',
    borderRadius: 16, color: 'rgba(255,255,255,0.7)', fontSize: 14, cursor: 'pointer',
  },
  logoutBtn: {
    background: 'none', border: 'none',
    color: 'rgba(255,255,255,0.2)', fontSize: 13, cursor: 'pointer',
    textAlign: 'center' as const, padding: '8px', width: '100%',
  },
};
